// Service pour gérer la synchronisation Steam (playtime + achievements)
import { supabase } from '@/integrations/supabase/client';
import { getSteamApiData } from './steamPlaytimeService';

export interface SteamGameStats {
  id: string;
  player_id: string;
  game_id: string;
  steam_game_id: number;
  total_achievements: number;
  unlocked_achievements: number;
  playtime_forever: number;
  playtime_2weeks: number;
  last_updated: string;
}

// Vérifier si un sync est nécessaire (dernier sync il y a plus de 24h)
export async function shouldSyncSteamData(playerId: string): Promise<boolean> {
  try {
    const { data: syncData } = await supabase
      .from('steam_sync_tracking')
      .select('last_sync_at, sync_in_progress')
      .eq('player_id', playerId)
      .maybeSingle();

    // Si jamais de sync ou sync en cours, ne pas synchroniser
    if (!syncData || syncData.sync_in_progress) {
      return !syncData; // Sync uniquement si jamais de sync
    }

    // Si le dernier sync date d'hier ou avant (vérifier la date, pas l'heure exacte)
    if (syncData.last_sync_at) {
      const lastSync = new Date(syncData.last_sync_at);
      const now = new Date();
      
      // Comparer les dates (année, mois, jour) plutôt que les heures exactes
      const lastSyncDate = new Date(lastSync.getFullYear(), lastSync.getMonth(), lastSync.getDate());
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Sync si la dernière sync n'était pas aujourd'hui
      return lastSyncDate.getTime() < today.getTime();
    }

    return true;
  } catch (error) {
    console.error('Erreur lors de la vérification du sync:', error);
    return false;
  }
}

// Démarrer le sync Steam (marquer comme en cours)
export async function startSteamSync(playerId: string): Promise<void> {
  try {
    await supabase
      .from('steam_sync_tracking')
      .upsert({
        player_id: playerId,
        sync_in_progress: true,
        sync_status: 'in_progress',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'player_id'
      });
  } catch (error) {
    console.error('Erreur lors du démarrage du sync:', error);
  }
}

// Finaliser le sync Steam (marquer comme terminé)
export async function completeSteamSync(playerId: string, gamesSynced: number): Promise<void> {
  try {
    await supabase
      .from('steam_sync_tracking')
      .update({
        sync_in_progress: false,
        sync_status: 'completed',
        last_sync_at: new Date().toISOString(),
        games_synced: gamesSynced,
        updated_at: new Date().toISOString()
      })
      .eq('player_id', playerId);
  } catch (error) {
    console.error('Erreur lors de la finalisation du sync:', error);
  }
}

// Marquer le sync comme échoué
export async function failSteamSync(playerId: string, error: string): Promise<void> {
  try {
    await supabase
      .from('steam_sync_tracking')
      .update({
        sync_in_progress: false,
        sync_status: 'failed',
        last_error: error.substring(0, 500), // Limiter la longueur
        updated_at: new Date().toISOString()
      })
      .eq('player_id', playerId);
  } catch (err) {
    console.error('Erreur lors de l\'échec du sync:', err);
  }
}

// Synchroniser les données Steam pour un joueur
export async function syncSteamDataForPlayer(playerId: string): Promise<void> {
  try {
    // Marquer le sync comme en cours
    await startSteamSync(playerId);

    // Récupérer tous les jeux achetés avec steam_game_id
    const { data: purchasedKeys } = await supabase
      .from('purchased_keys')
      .select(`
        id,
        game_id,
        game:games!inner(steam_game_id)
      `)
      .eq('player_id', playerId);

    if (!purchasedKeys || purchasedKeys.length === 0) {
      await completeSteamSync(playerId, 0);
      return;
    }

    let gamesSynced = 0;

    // Pour chaque jeu, récupérer les données Steam
    for (const purchase of purchasedKeys) {
      const steamGameId = (purchase.game as any)?.steam_game_id;
      if (steamGameId) {
        try {
          const steamData = await getSteamApiData(playerId, steamGameId);
          
          if (steamData) {
            // Calculer le pourcentage d'achievements une fois et le stocker
            const totalAchievements = steamData.total_achievements || 0;
            const unlockedAchievements = steamData.unlocked_achievements || 0;
            const achievementPercentage = totalAchievements > 0
              ? Math.round((unlockedAchievements / totalAchievements) * 100)
              : 0;
            
            // Stocker ou mettre à jour les stats
            await supabase
              .from('steam_game_stats')
              .upsert({
                player_id: playerId,
                game_id: purchase.game_id,
                steam_game_id: steamGameId,
                total_achievements: totalAchievements,
                unlocked_achievements: unlockedAchievements,
                achievement_percentage: achievementPercentage,
                playtime_forever: steamData.playtime_forever || 0,
                playtime_2weeks: steamData.playtime_2weeks || 0,
                last_updated: new Date().toISOString()
              }, {
                onConflict: 'player_id,game_id'
              });
            
            gamesSynced++;
          }
        } catch (error) {
          // Ignorer les erreurs pour un jeu spécifique
          console.error(`Erreur pour le jeu ${steamGameId}:`, error);
        }
      }
    }

    // Marquer le sync comme terminé
    await completeSteamSync(playerId, gamesSynced);
  } catch (error) {
    // Marquer le sync comme échoué
    await failSteamSync(playerId, error instanceof Error ? error.message : 'Unknown error');
    throw error;
  }
}

// Synchroniser les données Steam pour un créateur (basé sur key_requests approuvées)
export async function syncSteamDataForCreator(creatorId: string): Promise<void> {
  try {
    // Marquer le sync comme en cours
    await startSteamSync(creatorId);

    // Récupérer tous les jeux avec clés approuvées avec steam_game_id
    const { data: approvedRequests } = await supabase
      .from('key_requests')
      .select(`
        id,
        game_id,
        game:games!inner(steam_game_id)
      `)
      .eq('creator_id', creatorId)
      .eq('status', 'approved');

    if (!approvedRequests || approvedRequests.length === 0) {
      await completeSteamSync(creatorId, 0);
      return;
    }

    let gamesSynced = 0;

    // Pour chaque jeu, récupérer les données Steam
    for (const request of approvedRequests) {
      const steamGameId = (request.game as any)?.steam_game_id;
      if (steamGameId) {
        try {
          const steamData = await getSteamApiData(creatorId, steamGameId);
          
          if (steamData) {
            // Calculer le pourcentage d'achievements une fois et le stocker
            const totalAchievements = steamData.total_achievements || 0;
            const unlockedAchievements = steamData.unlocked_achievements || 0;
            const achievementPercentage = totalAchievements > 0
              ? Math.round((unlockedAchievements / totalAchievements) * 100)
              : 0;
            
            // Stocker ou mettre à jour les stats
            await supabase
              .from('steam_game_stats')
              .upsert({
                player_id: creatorId,
                game_id: request.game_id,
                steam_game_id: steamGameId,
                total_achievements: totalAchievements,
                unlocked_achievements: unlockedAchievements,
                achievement_percentage: achievementPercentage,
                playtime_forever: steamData.playtime_forever || 0,
                playtime_2weeks: steamData.playtime_2weeks || 0,
                last_updated: new Date().toISOString()
              }, {
                onConflict: 'player_id,game_id'
              });
            
            gamesSynced++;
          }
        } catch (error) {
          // Ignorer les erreurs pour un jeu spécifique
          console.error(`Erreur pour le jeu ${steamGameId}:`, error);
        }
      }
    }

    // Marquer le sync comme terminé
    await completeSteamSync(creatorId, gamesSynced);
  } catch (error) {
    // Marquer le sync comme échoué
    await failSteamSync(creatorId, error instanceof Error ? error.message : 'Unknown error');
    throw error;
  }
}

// Récupérer les stats Steam depuis la BDD
export async function getSteamStatsFromDB(playerId: string): Promise<SteamGameStats[]> {
  try {
    const { data, error } = await supabase
      .from('steam_game_stats')
      .select('*')
      .eq('player_id', playerId)
      .order('last_updated', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des stats Steam:', error);
    return [];
  }
}

